import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router";
import { User, LogOut } from "lucide-react";
import { useLogoutMutation } from "@/features/auth";

interface UserAvatarProps {
  initials?: string;
  name?: string;
  email?: string;
}

export function UserAvatar({
  initials = "U",
  name = "Usuário",
  email = "usuario@evoramaxx.com.br",
}: UserAvatarProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [logout] = useLogoutMutation();
  const navigate = useNavigate();

  // Fecha ao clicar fora
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Fecha ao pressionar Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        aria-label={name}
        aria-expanded={open}
        aria-haspopup="true"
        title={name}
        onClick={() => setOpen((v) => !v)}
        className="bg-primary hover:ring-secondary flex h-8 w-8 items-center justify-center rounded-full text-[12px] font-semibold text-white transition-all hover:ring-2"
      >
        {initials}
      </button>

      {open && (
        <div role="menu" aria-label="Menu do usuário" className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50">
          {/* Info do usuário */}
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-900">{name}</p>
            <p className="text-xs text-gray-500">{email}</p>
          </div>

          {/* Meu Perfil */}
          <div className="py-1">
            <button
              onClick={() => {
                setOpen(false);
                navigate("/meu-perfil");
              }}
              role="menuitem"
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <User size={16} />
              Meu Perfil
            </button>
          </div>

          {/* Sair */}
          <div className="border-t border-gray-100 py-1 mt-1">
            <button
              onClick={async () => {
                setOpen(false);
                await logout();
                navigate("/login");
              }}
              role="menuitem"
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
            >
              <LogOut size={16} />
              Sair
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
